import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { localStorageMethod } from 'src/common/config/FunctionHelper';
import { useGetUserAgentDetails } from 'src/content/orbitAi/hooks/useGetUserAgentDetails';
import { UserAgentDetailsResponse } from 'src/content/orbitAi/types/user-agents.type';

export interface UseOrbitAIIntercomProps {
  /** Optional site ID to fetch agent details for. If omitted, falls back to Redux or localStorage. */
  siteId?: string;
  /** Optional user details object. If omitted, falls back to Redux userDetails. */
  userDetails?: any;
  /** Explicit override for whether Orbit AI add-on is purchased. */
  isOrbitAiAddonPurchased?: boolean;
}

export interface UseOrbitAIIntercomReturn {
  /** Flag indicating whether the Orbit AI add-on has been purchased */
  orbit_purchased: boolean;
  /** Flag indicating whether the Orbit AI add-on is used/enabled */
  orbit_used: boolean;
  /** Loading state of the user agent details API */
  // isLoading: boolean;
  /** Error state if the API call fails */
  // isError: boolean;
  /** Error object if any */
  // error: Error | null;
  /** Full API response data */
  // userAgentDetails?: UserAgentDetailsResponse;
  /** Refetch function to re-trigger the API request */
  // refetch?: () => void;
}

/**
 * Hook to check if the user has purchased the Orbit AI add-on (`orbit_purchased`)
 * and whether the Orbit AI add-on has been used/configured (`orbit_used`).
 *
 * It checks the API response from `/api/v2/user/agents/:siteId` to determine
 * if Orbit AI is enabled. If enabled, `orbit_used` is true; otherwise false.
 *
 * @param props Optional props for siteId, userDetails, or isOrbitAiAddonPurchased
 * @returns {UseOrbitAIIntercomReturn} An object containing `orbit_purchased` and `orbit_used` flags
 */
export const useOrbitAIIntercom = (
  props?: UseOrbitAIIntercomProps
): UseOrbitAIIntercomReturn => {
  // 1. Resolve user details & purchase status from Redux or props
  const reduxUserDetails = useSelector(
    (state: RootState) => state.counter?.userDetails
  ) as any;
  const reduxIsAiAddonPurchased = useSelector(
    (state: RootState) => state.dashboard?.isAiAddonPurchased
  );

  const effectiveUserDetails = props?.userDetails ?? reduxUserDetails;

  const orbit_purchased = Boolean(
    props?.isOrbitAiAddonPurchased ??
      effectiveUserDetails?.isOrbitAiAddonPurchased ??
      effectiveUserDetails?.is_orbit_ai_addon_purchased ??
      effectiveUserDetails?.orbit_purchased ??
      reduxIsAiAddonPurchased ??
      false
  );

  // 2. Resolve siteId from props, Redux store, or localStorage
  const siteAuditDetails = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) as any;
  const reduxSiteId = siteAuditDetails?._id || siteAuditDetails?.site_id;

  const effectiveSiteId =
    props?.siteId ||
    reduxSiteId ||
    localStorageMethod('get', 'squareSpaceSiteId') ||
    '';

  // 3. Fetch agent details using the user agents endpoint (/api/v2/user/agents/:siteId)
  const {
    data: userAgentDetails,
    isLoading,
    isError,
    error,
    refetch
  } = useGetUserAgentDetails(effectiveSiteId);

  // 4. Check if Orbit AI / Local SEO has been used (enabled in API response)
  const orbitAiConfig = userAgentDetails?.response?.orbit_ai;
  const localSeoData = userAgentDetails?.response?.local_seo;
  const responseData: any = userAgentDetails?.response;

  const isLocalSeoEnabled = Boolean(
    Array.isArray(localSeoData)
      ? localSeoData.some((item: any) => item?.enabled)
      : localSeoData?.enabled
  );

  const isEnabled = Boolean(
    orbitAiConfig?.enabled ||
      orbitAiConfig?.keyword_assist?.enabled ||
      orbitAiConfig?.content_strategy?.enabled ||
      orbitAiConfig?.blog_generation?.enabled ||
      isLocalSeoEnabled ||
      responseData?.enabled ||
      (userAgentDetails as any)?.enabled ||
      false
  );

  const orbit_used = isEnabled;

  return {
    orbit_purchased,
    orbit_used,
    // isLoading,
    // isError,
    // error,
    // userAgentDetails,
    // refetch
  };
};

// Also export alias in case of typo backwards-compatibility
export const useOrbitAIIntercon = useOrbitAIIntercom;

export default useOrbitAIIntercom;